class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Short debounce so checkbox taps feel instant (was 500ms).
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 120);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    if (window.__egjFacetsPopstate) return;
    window.__egjFacetsPopstate = true;
    FacetFiltersForm.hasPopstateListener = true;

    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static setLoading(isLoading) {
    const grid = document.getElementById('ProductGridContainer');
    const collection = grid?.querySelector('.collection');
    if (collection) collection.classList.toggle('loading', isLoading);
    if (grid) {
      grid.classList.toggle('is-loading', isLoading);
      grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    document.querySelectorAll('.product-count__text').forEach((el) => {
      el.classList.toggle('loading', isLoading);
    });

    // Keep filter UI fully opaque while the grid loads (no is-loading dim).
    document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile, #FacetSortForm, #FacetSortFormMobileBar, .egj-filters').forEach((el) => {
      el.classList.remove('is-loading');
      el.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    });

    document.dispatchEvent(
      new CustomEvent('egj:loading', { detail: { busy: isLoading } })
    );
  }

  static finishRender(generation) {
    if (generation != null && generation !== FacetFiltersForm.renderGeneration) return;
    FacetFiltersForm.setLoading(false);
    document.dispatchEvent(new CustomEvent('facet:updated'));
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const generation = ++FacetFiltersForm.renderGeneration;
    FacetFiltersForm.setLoading(true);

    if (!sections.length) {
      FacetFiltersForm.setLoading(false);
      return;
    }

    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }
    FacetFiltersForm.abortController = new AbortController();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;
      const cached = FacetFiltersForm.filterData.find(filterDataUrl);

      if (cached) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, generation);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, generation);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static storeFilterCache(url, html) {
    FacetFiltersForm.filterData = FacetFiltersForm.filterData.filter((item) => item.url !== url);
    FacetFiltersForm.filterData.push({ html, url });
    const maxCache = 24;
    if (FacetFiltersForm.filterData.length > maxCache) {
      FacetFiltersForm.filterData.splice(0, FacetFiltersForm.filterData.length - maxCache);
    }
  }

  static applyFacetRender(doc, event) {
    FacetFiltersForm.renderFilters(doc, event);
    FacetFiltersForm.renderProductGridContainer(doc);
    FacetFiltersForm.renderProductCount(doc);
  }

  static renderSectionFromFetch(url, event, generation) {
    fetch(url, { signal: FacetFiltersForm.abortController && FacetFiltersForm.abortController.signal })
      .then(response => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((responseText) => {
        if (generation !== FacetFiltersForm.renderGeneration) return;
        FacetFiltersForm.storeFilterCache(url, responseText);
        // Parse once (was 3× DOMParser per response).
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        FacetFiltersForm.applyFacetRender(doc, event);
        FacetFiltersForm.finishRender(generation);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') {
          if (generation === FacetFiltersForm.renderGeneration) {
            FacetFiltersForm.setLoading(false);
          }
          return;
        }
        if (generation !== FacetFiltersForm.renderGeneration) return;
        FacetFiltersForm.setLoading(false);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, generation) {
    if (generation !== FacetFiltersForm.renderGeneration) return;
    const cached = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cached) {
      FacetFiltersForm.setLoading(false);
      return;
    }
    const doc = new DOMParser().parseFromString(cached.html, 'text/html');
    FacetFiltersForm.applyFacetRender(doc, event);
    FacetFiltersForm.finishRender(generation);
  }

  static renderProductGridContainer(doc) {
    const source = doc.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
  }

  static renderProductCount(doc) {
    const countEl = doc.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!countEl) return;

    if (container) {
      container.innerHTML = countEl.innerHTML;
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = countEl.innerHTML;
    }
    const containerMobileBar = document.getElementById('ProductCountMobileBar');
    if (containerMobileBar) {
      containerMobileBar.innerHTML = countEl.innerHTML;
    }
  }

  static renderFilters(doc, event) {
    const facetDetailsElements =
      doc.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const current = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (current) {
        current.innerHTML = element.innerHTML;
        current.className = element.className;
      }
    });

    FacetFiltersForm.renderActiveFacets(doc);
    FacetFiltersForm.renderAdditionalElements(doc);

    if (countsToRender) {
      const currentFilter = event && event.target && event.target.closest('.js-filter');
      if (currentFilter) FacetFiltersForm.renderCounts(countsToRender, currentFilter);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (!target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
      const isEmpty = !activeFacetsElement.querySelector('facet-remove, .egj-filters__chip, .active-facets__button');
      target.classList.toggle('is-empty', isEmpty);
      if (selector === '.active-facets-desktop') {
        const clearWrap = document.querySelector('.egj-filters__clear-wrap');
        if (clearWrap) clearWrap.classList.toggle('is-empty', isEmpty);
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm && mobileForm.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      targetElementAccessibility.outerHTML = sourceElementAccessibility.outerHTML;
    }

    /* The header carries the Clear link, which only renders once a value is
       active; it holds no inputs, so swapping it keeps focus in the list. */
    const targetHeader = target.querySelector('.facets__header');
    const sourceHeader = source.querySelector('.facets__header');
    if (targetHeader && sourceHeader) targetHeader.outerHTML = sourceHeader.outerHTML;

    if (target.matches('details') && source.className) target.className = source.className;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return [
      {
        section: grid ? grid.dataset.id : document.getElementById('ProductGridContainer')?.dataset?.id,
      }
    ].filter((s) => s.section);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value === '' || value == null) continue;
      params.append(key, value);
    }
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const sourceForm = event.target.closest('form');
    if (!sourceForm) return;

    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(sourceForm)
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = sourceForm.id === 'FacetFiltersFormMobile';
      const isMobileSortBar = sourceForm.id === 'FacetSortFormMobileBar';

      sortFilterForms.forEach((form) => {
        if (isMobile) {
          if (form.id === 'FacetFiltersFormMobile') {
            forms.push(this.createSearchParams(form));
          }
        } else if (isMobileSortBar) {
          if (form.id === 'FacetSortFormMobileBar') {
            forms.push(this.createSearchParams(form));
          }
          if (form.id === 'FacetFiltersForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.renderGeneration = 0;
FacetFiltersForm.abortController = null;
FacetFiltersForm.hasPopstateListener = false;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
FacetFiltersForm.setListeners();

if (!window.__egjFacetsDocClick) {
window.__egjFacetsDocClick = true;
document.addEventListener('click', (event) => {
  const closeTrigger = event.target.closest('[data-egj-close-filters]');
  if (closeTrigger) {
    event.preventDefault();
    event.stopPropagation();
    const disclosure =
      closeTrigger.closest('.mobile-facets__disclosure') ||
      document.querySelector('.egj-shop .mobile-facets__disclosure');
    if (!disclosure) return;

    const menuDrawer = disclosure.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.closeMenuDrawer === 'function') {
      menuDrawer.closeMenuDrawer(event, closeTrigger);
    }

    disclosure.removeAttribute('open');
    disclosure.classList.remove('menu-opening');
    document.body.classList.remove('overflow-hidden-mobile');
    return;
  }

  const trigger = event.target.closest('[data-egj-open-filters]');
  if (!trigger) return;
  event.preventDefault();

  const disclosure = document.querySelector('.egj-shop .mobile-facets__disclosure');
  const summary = disclosure && disclosure.querySelector(':scope > summary');
  if (!summary || !disclosure) return;

  const wasOpen = disclosure.hasAttribute('open');
  summary.click();

  if (wasOpen) return;

  // Guarantee visible open state: focus can bounce off the clipped summary and
  // menu-drawer focusout may strip .menu-opening while [open] remains.
  requestAnimationFrame(() => {
    disclosure.classList.add('menu-opening');
    document.body.classList.add('overflow-hidden-mobile');
    const heading = disclosure.querySelector('.mobile-facets__heading');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      try {
        heading.focus({ preventScroll: true });
      } catch (e) {
        heading.focus();
      }
    }
  });
});
}
class PriceRange extends HTMLElement {
  constructor() {
    super();
    /* Grill King dual-handle slider owns its own sync — skip Dawn CE. */
    if (this.hasAttribute('data-egj-price')) return;
    this.querySelectorAll('input.egj-filters__price-input, input[type="number"]')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    if (this.hasAttribute('data-egj-price')) return;
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    if (this.hasAttribute('data-egj-price')) return;
    const inputs = this.querySelectorAll('input.egj-filters__price-input, input[type="number"]');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    if (!input || input.type === 'range') return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (Number.isNaN(value)) {
      input.value = '';
      return;
    }
    if (!Number.isNaN(min) && value < min) input.value = min;
    if (!Number.isNaN(max) && value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') {
      form.onActiveFilterClick(event);
    }
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}
